import { useTranslations } from '@vocab/react';
import translations from './.vocab';
import React from 'react';
import { AdPriceDetails } from '../AdPriceDetails/AdPriceDetails';
import { Text, Alert, Stack } from 'braid-design-system';
import { PriceVisibility } from '../../utils/Product';
import { AdvertiserNation } from '../../utils/AdvertiserNation';
import { useHirerPriceVisibility } from '../../services/hirerAccountService';
import { format } from 'date-fns';
import { usePricingContext } from 'src/App/context/PricingContextProvider';

interface PriceAttribute {
  text?: string;
  id?: number;
}

interface PriceAttributes {
  roleTitle?: string;
  subClassification?: string;
  location?: PriceAttribute;
}

interface AdPriceSummaryProps {
  advertiserId?: string;
  priceAttributes: PriceAttributes;
  advertiserNation: AdvertiserNation;
}

const AdPriceSummary = ({
  advertiserId = '',
  priceAttributes: { roleTitle = '', subClassification = '', location = {} },
  advertiserNation,
}: AdPriceSummaryProps) => {
  const { t } = useTranslations(translations);
  const { isPreview } = usePricingContext();

  const locationId =
    typeof location.id === 'string'
      ? parseFloat(location.id)
      : (location.id as number);

  const {
    loading: priceVisibilityLoading,
    data: priceVisibilityData,
    error: priceVisibilityError,
  } = useHirerPriceVisibility(advertiserId);

  const showDiscountedPrice =
    !isPreview &&
    !priceVisibilityLoading &&
    !priceVisibilityError &&
    priceVisibilityData === PriceVisibility.SHOW_DISCOUNTED_PRICE;

  const currentDate = format(Date.now(), 'd/M/yyyy');
  return (
    <Stack space={'xlarge'}>
      <AdPriceDetails
        advertiserId={advertiserId}
        roleTitle={roleTitle}
        subClassificationId={subClassification}
        locationId={locationId}
        locationText={location.text || ''}
        advertiserNation={advertiserNation}
        showDiscountedPrice={showDiscountedPrice}
      />
      <Stack space={'small'}>
        <Text tone="secondary" size="standard">
          {t('*Current prices as at currentDate and are subject to change', {
            currentDate,
          })}
        </Text>
        {showDiscountedPrice && (
          <Alert tone="info">
            <Text>{t('All prices reflect your current...')}</Text>
          </Alert>
        )}
      </Stack>
    </Stack>
  );
};

export {
  type PriceAttribute,
  type PriceAttributes,
  AdPriceSummary,
  type AdPriceSummaryProps,
};
